import React, { useState } from 'react';
import { Image, Tooltip, Modal } from 'antd';
import {
  PictureOutlined,
  RightCircleOutlined,
  LeftOutlined,
  RightOutlined,
  CloseOutlined
} from '@ant-design/icons';

const ProductImagesGallery = ({ 
  skuItem, 
  maxVisibleImages = 2, 
  showRemainingCount = true,
  imageSize = 35 
}) => {
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!skuItem?.images || skuItem.images.length === 0) {
    return <span className="no-line-through">-</span>;
  }

  // Get thumbnail images for preview
  const thumbnailImages = skuItem.images.filter(img => img.isThumbnail || img.isMain);
  const allImages = skuItem.images.filter(img => !img.isThumbnail).map(img => ({
    src: img.url,
    width: img.width || 500,
    height: img.height || 500,
    alt: 'Product image',
    isMain: img.isMain
  }));

  // Sort images to show main images first
  allImages.sort((a, b) => {
    if (a.isMain && !b.isMain) return -1;
    if (!a.isMain && b.isMain) return 1;
    return 0;
  });

  const openGallery = (index = 0) => {
    setCurrentImageIndex(index);
    setGalleryVisible(true);
  };

  // Navigation functions for gallery
  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? allImages.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === allImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handleCloseGallery = () => {
    setGalleryVisible(false);
  };

  return (
    <>
      <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
        {allImages.slice(0, maxVisibleImages).map((img, index) => (
          <Image
            key={index}
            src={img.src}
            alt={`Product image ${index + 1}`}
            width={imageSize}
            height={imageSize}
            style={{ objectFit: 'contain', cursor: 'pointer', borderRadius: '4px', border: '1px solid #e2e2e2' }}
            onClick={() => openGallery(index)}
            preview={false}
          />
        ))}
        {allImages.length > maxVisibleImages && showRemainingCount && (
          <Tooltip title={`${allImages.length - maxVisibleImages} more images`}>
            <a 
              onClick={() => openGallery(maxVisibleImages)}
            >
              +{allImages.length - maxVisibleImages}
            </a>
          </Tooltip>
        )}
      </div>

      {galleryVisible && allImages.length > 0 && (
        <Modal
          open={galleryVisible}
          onCancel={handleCloseGallery}
          footer={null}
          width={800}
          centered
          styles={{
            mask: { backgroundColor: 'rgba(0, 0, 0, 0.85)' },
            content: { 
              backgroundColor: 'transparent',
              boxShadow: 'none',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }
          }}
        >
          <div style={{ 
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {/* Close button */}
            <div 
              style={{ 
                position: 'absolute', 
                top: '-40px', 
                right: '-20px', 
                cursor: 'pointer',
                color: 'white',
                fontSize: '24px',
                zIndex: 1001
              }}
              onClick={handleCloseGallery}
            >
              <CloseOutlined />
            </div>

            {/* Main image */}
            <div style={{ 
              maxWidth: '90%', 
              maxHeight: '80vh',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <img 
                src={allImages[currentImageIndex]?.src} 
                alt={`Product image ${currentImageIndex + 1}`}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
            </div>

            {/* Navigation buttons */}
            {allImages.length > 1 && (
              <>
                <div 
                  style={{ 
                    position: 'absolute', 
                    left: '10px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '18px'
                  }}
                  onClick={handlePrevImage}
                >
                  <LeftOutlined />
                </div>
                <div 
                  style={{ 
                    position: 'absolute', 
                    right: '10px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '18px'
                  }}
                  onClick={handleNextImage}
                >
                  <RightOutlined />
                </div>
              </>
            )}

            {/* Image counter */}
            <div style={{ 
              position: 'absolute', 
              bottom: '-30px', 
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              fontSize: '14px'
            }}>
              {currentImageIndex + 1} / {allImages.length}
            </div>

            {/* Thumbnail navigation */}
            {allImages.length > 1 && (
              <div style={{ 
                position: 'absolute', 
                bottom: '-70px',
                display: 'flex',
                gap: '8px',
                justifyContent: 'center',
                overflowX: 'auto',
                width: '100%',
                padding: '10px 0'
              }}>
                {allImages.slice(0, 5).map((img, idx) => (
                  <img 
                    key={idx}
                    src={img.src}
                    alt={`Thumbnail ${idx}`}
                    style={{ 
                      width: '40px',
                      height: '40px',
                      objectFit: 'cover',
                      cursor: 'pointer',
                      opacity: idx === currentImageIndex ? 1 : 0.6,
                      border: idx === currentImageIndex ? '2px solid white' : 'none',
                    }}
                    onClick={() => setCurrentImageIndex(idx)}
                  />
                ))}
                {allImages.length > 5 && (
                  <div style={{
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    borderRadius: '2px'
                  }}>
                    +{allImages.length - 5}
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default ProductImagesGallery;